import {
  ClientPostTypeEnum,
  type ClientGeoArea
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { MoreVerticalIcon, AccountBalanceIcon, PersonIcon } from '@/assets';
import { APP_URL } from '@/constants/app';
import { useGetOrganizationById } from '@/features/api/hooks/grapevine-organization/useGetOrganizationById';
import { useGetPostById } from '@/features/api/hooks/grapevine-post/useGetPostById';
import { useDeletePost } from '@/features/api/hooks/grapevine-post-events/useDeletePost';
import { useGetGeoArea } from '@/features/api/hooks/participant-geo-area/useGetGeoArea';
import { useBlockPerson } from '@/features/api/hooks/participant-person-block-events/useBlockPerson';
import { useUnblockPerson } from '@/features/api/hooks/participant-person-block-events/useUnblockPerson';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { useFlag } from '@/features/flag/providers/useFlag';
import { FlagEntityTypeEnum } from '@/features/flag/types/flagEntityType';
import { GeoAreaRoute } from '@/features/geo-area/navigation/geoAreaRoute';
import { useBottomModal } from '@/features/popup/provider/useBottomModal';
import { usePopup } from '@/features/popup/provider/usePopup';
import { PostCreatorActions } from '@/features/posts/components/detail/PostCreatorActions';
import { PostDetailHeaderTitle } from '@/features/posts/components/detail/PostDetailHeaderTitle';
import { PostViewerActions } from '@/features/posts/components/detail/PostViewerActions';
import { SuggestionWithdrawView } from '@/features/posts/components/detail/suggestion/SuggestionWithdrawView';
import { MAX_POST_PREVIEW_LENGTH } from '@/features/posts/constants/params';
import { PostsRoute } from '@/features/posts/navigation/postsRoute';
import type { GenericPostDetail } from '@/features/posts/types/posts';
import { INVITE_URL } from '@/features/profile-setting/constants/links';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationBar } from '@/navigation/components/navbar/NavigationBar';
import type { AppNativeStackScreenProps } from '@/navigation/types/navigation';
import { AppRoute } from '@/navigation/types/navigation';
import { OrganizationAccessState } from '@/shared/components/organization/organizationAccess';
import { CONTENT_MANAGER_ROLES } from '@/shared/components/organization/roles';
import { useOrganizationAccessFor } from '@/shared/components/organization/useOrganizationAccessFor';
import { PastTime } from '@/shared/components/PastTime';
import { share } from '@/shared/utils/share/share';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type PostDetailHeaderProps = {
  postId?: string;
  navigation: AppNativeStackScreenProps<
    typeof AppRoute.PostDetail
  >['navigation'];
};

export const PostDetailHeader: FC<PostDetailHeaderProps> = ({
  postId,
  navigation
}) => {
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'screens.post-detail'
  });
  const { userId, homeGeoAreaId } = useAppState();
  const { data: post, refetch } = useGetPostById(postId);
  const { data: organization } = useGetOrganizationById(post?.organizationId);
  // TODO: Klären, wie damit umgegangen werden soll, dass es mehrere geoAreaIds gibt.
  const { data: geoArea } = useGetGeoArea(
    post?.isCivilProtection &&
      homeGeoAreaId &&
      post?.geoAreaIds?.includes(homeGeoAreaId)
      ? homeGeoAreaId
      : post?.geoAreaIds?.[0]
  );

  const { showAlert } = usePopup();
  const { clearAllBottomModals, showBottomModal } = useBottomModal();

  const { flag } = useFlag();

  const theme = useAppTheme();
  const deletePost = useDeletePost();
  const blockPerson = useBlockPerson();
  const unblockPerson = useUnblockPerson();
  const isBlocked = post?.isBlocked;
  const isUserPostCreator = post?.creator?.id === userId;
  const isOrganizationPost = !isEmptyOrNull(post?.organizationId);
  const orgAccess = useOrganizationAccessFor(organization, {
    roles: CONTENT_MANAGER_ROLES
  });
  const canManagePost =
    isUserPostCreator ||
    (isOrganizationPost && orgAccess === OrganizationAccessState.Granted);
  const isCreatorBlocked = !!(post?.creator && isBlocked);

  const postType = post?.postType;
  const title = post?.title;
  const text = post?.text;
  const creatorId = post?.creator?.id;
  const creatorName = post?.creator?.firstName;
  const geoAreaName = geoArea?.name;

  const displayName = isOrganizationPost
    ? (organization?.name ?? post?.creatorOrSiteName)
    : post?.creatorOrSiteName;
  const displayPicture = isOrganizationPost
    ? organization?.logoImage
    : post?.creatorOrSitePicture;

  const handleSharePost = useCallback(async () => {
    const shareTitle = t('share.title', { creatorName, geoAreaName });

    const content = [title, text]
      .filter((t) => !isEmptyOrNull(t))
      .join('\n')
      .slice(0, MAX_POST_PREVIEW_LENGTH);

    const url = postId ? `${APP_URL}${PostsRoute.PostDetail}?id=${postId}` : '';
    const postUrl = isEmptyOrNull(url)
      ? ''
      : t('share.message.postUrl', { postUrl: url });

    const getApp = t('share.message.getApp');

    const message = [shareTitle, content, postUrl, getApp]
      .filter((t) => !isEmptyOrNull(t))
      .join('\n\n');

    await share({ message, url: INVITE_URL, note: t('share.note') });
  }, [t, creatorName, geoAreaName, title, text, postId]);

  const handleChat = useCallback(() => {
    if (isEmptyOrNull(postId)) return;
    navigation.navigate(AppRoute.NewChatOnPost, { postId });
  }, [navigation, postId]);

  const handleFlag = useCallback(() => {
    if (isEmptyOrNull(postId)) return;
    flag(FlagEntityTypeEnum.Post, postId);
  }, [flag, postId]);

  const handleBlock = useCallback(() => {
    if (isEmptyOrNull(creatorId)) return;

    if (isBlocked) {
      // Todo: onError
      unblockPerson.mutate(creatorId, { onSuccess: () => refetch() });
      // Todo: onError
    } else {
      blockPerson.mutate(creatorId, { onSuccess: () => refetch() });
    }
  }, [blockPerson, unblockPerson, isBlocked, creatorId, refetch]);

  const handleEdit = useCallback(
    () => navigation.navigate(AppRoute.PostEditor, { id: postId ?? '' }),
    [navigation, postId]
  );

  const organizationId = post?.organizationId;
  const handleOrganizationPress = useCallback(() => {
    if (!organizationId) return;
    navigation.navigate(GeoAreaRoute.OrganizationProfile, {
      id: organizationId
    });
  }, [navigation, organizationId]);

  const handleDelete = useCallback(() => {
    if (isEmptyOrNull(postId)) return;
    if (postType === ClientPostTypeEnum.Suggestion) {
      showBottomModal({
        title: t('withdrawReason'),
        children: (
          <SuggestionWithdrawView
            suggestionId={postId}
            onWithdraw={() => {
              clearAllBottomModals();
              navigation.pop();
            }}
          />
        )
      });
    } else {
      showAlert({
        dismissable: true,
        confirmColor: theme.colors.error,
        labels: t('alerts.delete', { returnObjects: true }),
        onConfirm: () => {
          if (isEmptyOrNull(postId)) return;

          deletePost.mutate(postId, {
            onSuccess: () => {
              navigation.pop();
            }
          });
        }
      });
    }
  }, [
    clearAllBottomModals,
    deletePost,
    navigation,
    postId,
    postType,
    showAlert,
    showBottomModal,
    t,
    theme.colors.error
  ]);

  return (
    <NavigationBar
      picture={isBlocked ? undefined : displayPicture}
      pictureFallback={{
        icon: isOrganizationPost ? AccountBalanceIcon : PersonIcon
      }}
      onPicturePress={isOrganizationPost ? handleOrganizationPress : undefined}
      titleComponent={
        <PostDetailHeaderTitle
          displayName={displayName}
          isOrganizationPost={isOrganizationPost}
          subTitle={<PostSubtitle post={post} geoArea={geoArea} />}
        />
      }
      onTitlePress={isOrganizationPost ? handleOrganizationPress : undefined}
      buttons={[
        {
          name: t('options'),
          asMenu: true,
          icon: MoreVerticalIcon,
          menuItems: (onItemPress) =>
            canManagePost ? (
              <PostCreatorActions
                onItemPress={onItemPress}
                handleSharePost={handleSharePost}
                handleDelete={handleDelete}
                handleEdit={handleEdit}
                handleChat={isCreatorBlocked ? undefined : handleChat}
                chatDisabled={isUserPostCreator}
                withdrawInsteadOfDelete={
                  post.postType === ClientPostTypeEnum.Suggestion
                }
              />
            ) : (
              <PostViewerActions
                onItemPress={onItemPress}
                isCreatorBlocked={isCreatorBlocked}
                handleSharePost={handleSharePost}
                handleChat={handleChat}
                handleFlag={handleFlag}
                handleBlock={handleBlock}
              />
            )
        }
      ]}
    />
  );
};

const PostSubtitle: FC<{
  post?: GenericPostDetail;
  geoArea?: ClientGeoArea;
}> = ({ post, geoArea }) => (
  <>
    <PastTime time={post?.created} withoutSuffix />
    {isEmptyOrNull(post?.groupId) && (
      <>
        {'・'}
        {geoArea?.name}
      </>
    )}
  </>
);
